/* eslint-disable eqeqeq */
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  Briefcase,
  Building2,
  Calendar,
  Clock3,
  User,
  Users,
} from "lucide-react";
import Html from "../../utils/Html";
import API from "../../utils/Base.API";
import "./style.css";

const EVALUATION_ROWS = [
  {
    no: 1,
    key: "teamwork",
    title: "Teamwork & Warmth",
    definition:
      "Promotes and develops good working relationships with other employees. Assists others/treats people with respect. Makes the office/workplace a better place to be.",
  },
  {
    no: 2,
    key: "communication",
    title: "Communication",
    definition:
      "Speaks, listens and writes clearly and logically to share information with others. Thinks and plans carefully before communicating.",
  },
  {
    no: 3,
    key: "planning",
    title: "Planning & Organization",
    definition:
      "Displays commitment, plans ahead, organizes and prioritizes tasks and builds a sense of urgency towards work.",
  },
  {
    no: 4,
    key: "punctuality",
    title: "Punctuality",
    definition:
      "Follows company processes and policies. Completes work and tasks within deadlines. On time for work and informs when late/sick.",
  },
  {
    no: 5,
    key: "sustainability",
    title: "Sustainability",
    definition:
      "Is concerned for company resources, operating lean and reducing waste and unnecessary expenses as much as possible.",
  },
  {
    no: 6,
    key: "tools",
    title: "Use EXO Tools",
    definition:
      "Learns and uses EXO tools and systems daily to ensure consistent working methods and teaches others how to use them.",
  },
  {
    no: 7,
    key: "culture",
    title: "Live EXO Culture",
    definition:
      "Understands EXO Values, makes them a part of daily work life, promotes them to others and connects them to work as often as possible.",
  },
  {
    no: 8,
    key: "jobSkill",
    title: "Job Skill",
    definition: "Please specify role:",
    hasRoleSelect: true,
  },
  {
    no: 9,
    key: "trainingNeeds",
    title: "Training Needs",
    definition: "",
    hasTrainingInput: true,
  },
];

const SCORE_OPTIONS = [
  { value: "", label: "N/A" },
  { value: "1", label: "Poor" },
  { value: "2", label: "Below Average" },
  { value: "3", label: "Average" },
  { value: "4", label: "Good" },
  { value: "5", label: "Excellent" },
];

const JOB_SKILL_OPTIONS = [
  { value: "NA", label: "N/A" },
  { value: "ER", label: "Explorer" },
  { value: "LN", label: "Learner" },
  { value: "ID", label: "Independent" },
  { value: "EP", label: "Expert" },
];

const ASSESSMENT_NA_SENTINEL = "__NA__";

const ROLE_OPTIONS = [
  "Senior Travel Consultant",
  "Travel Consultant",
  "General Manager",
  "Hub Leader",
  "Operation Executive/ Officer",
  "Operation Supervisor",
  "Knowledge & Platform Specialist",
  "Learning & Systems Adoption Leader",
  "Reservation Officer",
  "Reservation Supervisor",
  "Others",
];
function probationApiUrl(path = "") {
  const cleanPath = String(path || "").replace(/^\//, "");
  return `/probation/api/${cleanPath}`;
}

const Probation = () => {
  const { userId } = useParams();
  const routeUserId = Number(userId);
  const normalizedUserId = Number.isNaN(routeUserId) ? 0 : routeUserId;
  const profile = window.$user_profile || {};
  const defaultProfileForm = mapProfileToForm(profile);

  const [isLoadingProfile, setIsLoadingProfile] = useState(
    () => !!profile?.idu,
  );
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fetchedProfile, setFetchedProfile] = useState(profile);
  const [evaluationRows, setEvaluationRows] = useState(EVALUATION_ROWS);
  const [assessmentOptions, setAssessmentOptions] = useState(SCORE_OPTIONS);
  const [jobSkillAssessmentOptions, setJobSkillAssessmentOptions] =
    useState(JOB_SKILL_OPTIONS);
  const [jobRoleOptions, setJobRoleOptions] = useState(ROLE_OPTIONS);
  const [submitStatus, setSubmitStatus] = useState("");
  const [submitStatusUrl, setSubmitStatusUrl] = useState("");
  const [isPageBlocked, setIsPageBlocked] = useState(false);
  const [isUserPrefillReady, setIsUserPrefillReady] = useState(false);
  const [submitDialog, setSubmitDialog] = useState({
    open: false,
    type: "processing",
    title: "",
    lines: [],
  });
  const [validationErrors, setValidationErrors] = useState({
    meetingDate: "",
    evaluation: "",
  });
  const [formState, setFormState] = useState({
    employeeName: defaultProfileForm.employeeName || "",
    supervisorName: defaultProfileForm.supervisorName || "",
    jobTitle: defaultProfileForm.jobTitle || "",
    department: defaultProfileForm.department || "",
    startDate: defaultProfileForm.startDate || "",
    meetingDate: "",
    additionalComment: "",
    passProbation: false,
  });
  const [evaluationState, setEvaluationState] = useState(() => {
    const state = {};
    EVALUATION_ROWS.forEach((row) => {
      state[row.key] = getInitialEvaluationRowState();
    });
    return state;
  });
  const [evaluatedTimestamp] = useState(() => formatNowTimestamp());
  const actorUserId = Number(profile?.idu) || 0;
  const probationFormRef = useRef(null);

  useEffect(() => {
    loadExternalStyles();
  }, []);

  useEffect(() => {
    if (!isUserPrefillReady) return;

    const loadTemplate = async () => {
      setIsLoadingTemplate(true);
      try {
        const res = await API.get(probationApiUrl("template"));
        const payload = parseApiPayload(res?.data);
        if (!isSuccessPayload(payload)) {
          const userError =
            payload?.err_msg ||
            payload?.data?.err_msg ||
            "Unable to load probation data.";
          setIsPageBlocked(true);
          setSubmitDialog({
            open: true,
            type: "error",
            title: "Access denied",
            lines: [userError],
          });
          return;
        }

        const data = payload?.data || payload;
        const templateItems = Array.isArray(data?.items) ? data.items : [];
        if (templateItems.length > 0) {
          setEvaluationRows((prevRows) =>
            mergeTemplateRows(prevRows, templateItems),
          );
        }

        const nextAssessmentOptions = normalizeOptions(
          data?.assessmentOptions,
          SCORE_OPTIONS,
        );
        const nextJobSkillOptions = normalizeOptions(
          data?.jobSkillAssessmentOptions,
          JOB_SKILL_OPTIONS,
        );
        const nextRoleOptions = normalizeOptions(
          data?.jobRoleOptions,
          ROLE_OPTIONS,
        );

        setAssessmentOptions(nextAssessmentOptions);
        setJobSkillAssessmentOptions(nextJobSkillOptions);
        setJobRoleOptions(nextRoleOptions.map((opt) => opt.label));
      } catch (err) {
        // use fallback options when template API is unavailable
      } finally {
        setIsLoadingTemplate(false);
      }
    };

    loadTemplate();
  }, [isUserPrefillReady]);

  useEffect(() => {
    if (!actorUserId) return;

    const fetchUserPrefill = async () => {
      setIsLoadingProfile(true);
      setIsPageBlocked(false);
      setIsUserPrefillReady(false);
      try {
        const res = await API.post(probationApiUrl("user"), {
          userId: normalizedUserId || profile?.idu || 0,
          actorUserId,
        });
        const payload = parseApiPayload(res?.data);
        if (!isSuccessPayload(payload)) {
          const userError =
            payload?.err_msg ||
            payload?.data?.err_msg ||
            "Unable to load probation data.";
          setIsPageBlocked(true);
          setSubmitDialog({
            open: true,
            type: "error",
            title: "Access denied",
            lines: [userError],
          });
          return;
        }

        const data = payload?.data || payload;
        const userData =
          data?.user && typeof data.user === "object" ? data.user : data;
        setFetchedProfile((prev) => ({ ...prev, ...userData }));
        setFormState((prev) => ({
          ...prev,
          ...mapProfileToForm(userData),
        }));
        setIsUserPrefillReady(true);
      } catch (err) {
        const fallbackMessage =
          err?.message || "Unable to load probation data.";
        setIsPageBlocked(true);
        setSubmitDialog({
          open: true,
          type: "error",
          title: "Access denied",
          lines: [fallbackMessage],
        });
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserPrefill();
  }, [actorUserId, normalizedUserId, profile?.idu]);
  useEffect(() => {
    const syncCommentTextareaMinHeight = () => {
      if (typeof document === "undefined") return;

      const rows = document.querySelectorAll(
        "#probation-page .probation-table tbody tr",
      );

      rows.forEach((rowEl) => {
        const commentCell = rowEl.querySelector("td:nth-child(5)");
        const textarea = commentCell?.querySelector(
          "textarea.probation-comment-textarea",
        );
        if (!commentCell || !textarea) return;

        const rowHeight = rowEl.getBoundingClientRect().height;
        const cellStyle = window.getComputedStyle(commentCell);
        const paddingTop = parseFloat(cellStyle.paddingTop || "0");
        const paddingBottom = parseFloat(cellStyle.paddingBottom || "0");
        const minHeight = Math.max(
          60,
          Math.floor(rowHeight - paddingTop - paddingBottom),
        );

        textarea.style.minHeight = `${minHeight}px`;
      });
    };

    syncCommentTextareaMinHeight();
    const raf = window.requestAnimationFrame(syncCommentTextareaMinHeight);
    window.addEventListener("resize", syncCommentTextareaMinHeight);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", syncCommentTextareaMinHeight);
    };
  }, [evaluationRows]);

  if (!profile?.idu) {
    return <Html.NoAccess />;
  }

  const activeProfile = fetchedProfile?.idu ? fetchedProfile : profile;
  const evaluatedByEmail =
    pickValue(activeProfile, ["emailadd", "email"]) || "-";
  const isPageLoading = isLoadingProfile || isLoadingTemplate;

  const handleFieldChange = (evt) => {
    const { name, value } = evt.target;
    setFormState((prev) => ({ ...prev, [name]: value }));

    if (name === "meetingDate" && value) {
      setValidationErrors((prev) => ({ ...prev, meetingDate: "" }));
    }
  };

  const handleEvaluationChange = (key, field, value) => {
    setEvaluationState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));

    if (value) {
      setValidationErrors((prev) => ({ ...prev, evaluation: "" }));
    }
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    submitAssessment();
  };

  const closeSubmitDialog = () => {
    setSubmitDialog((prev) => ({ ...prev, open: false }));
  };

  const submitAssessment = async () => {
    const missingMeetingDate = !String(formState.meetingDate || "").trim();
    const hasMissingEvaluation = evaluationRows.some((row) => {
      if (row.hasTrainingInput) return false;
      const current = evaluationState[row.key] || {};

      if (row.hasRoleSelect) {
        const jobSkill = String(current.jobSkill || "").trim();
        const role = String(current.role || "").trim();
        return !jobSkill || !role;
      }

      return !String(current.score || "").trim();
    });

    if (missingMeetingDate || hasMissingEvaluation) {
      setValidationErrors({
        meetingDate: missingMeetingDate ? "Please select meeting date." : "",
        evaluation: hasMissingEvaluation
          ? "Please select assessment for all evaluation items."
          : "",
      });
      setSubmitStatus("Please complete required fields before submit.");
      setSubmitStatusUrl("");
      probationFormRef.current?.reportValidity();
      return;
    }

    setValidationErrors({ meetingDate: "", evaluation: "" });
    setIsSubmitting(true);
    setSubmitStatus("");
    setSubmitStatusUrl("");
    setSubmitDialog({
      open: true,
      type: "processing",
      title: "Processing...",
      lines: ["Please wait while we submit your assessment."],
    });

    try {
      const items = evaluationRows.map((row) => {
        const current = evaluationState[row.key] || {};
        if (row.hasTrainingInput) {
          return {
            no: row.no,
            text: current.trainingNeeds || "",
          };
        }
        if (row.hasRoleSelect) {
          return {
            no: row.no,
            assessment: current.jobSkill || "NA",
            role: current.role || "",
            comment: current.comment || "",
          };
        }
        return {
          no: row.no,
          assessment:
            current.score === ASSESSMENT_NA_SENTINEL ? "" : current.score || "",
          comment: current.comment || "",
        };
      });

      const submitBody = {
        userId: normalizedUserId || profile?.idu || 0,
        actorUserId,
        submittedById: actorUserId,
        submittedByEmail: evaluatedByEmail,
        meetingDate: formState.meetingDate,
        passProbation: !!formState.passProbation,
        additionalComment: formState.additionalComment || "",
        items,
      };

      const submitRes = await API.post(probationApiUrl("submit"), submitBody);
      const submitPayload = parseApiPayload(submitRes?.data);
      if (!isSuccessPayload(submitPayload)) {
        throw new Error(submitPayload?.err_msg || "Submit failed");
      }

      const submitData = submitPayload?.data || submitPayload;
      const assessmentId = submitData?.assessmentId;
      const statusText = submitData?.status || "";
      const statusLabel = capitalizeFirstChar(statusText || "Completed");
      setSubmitStatus(assessmentId ? statusLabel : "Submitted successfully");

      if (assessmentId) {
        try {
          const statusRes = await API.get(
            probationApiUrl(`submit-status/${assessmentId}`),
            {
              params: { actorUserId },
            },
          );
          const statusPayload = parseApiPayload(statusRes?.data);
          if (isSuccessPayload(statusPayload)) {
            const statusData = statusPayload?.data || statusPayload;
            const nextStatus = statusData?.status || statusText || "";
            const nextStatusLabel = capitalizeFirstChar(
              nextStatus || "Completed",
            );
            const pdfUrl = statusData?.pdfUrl;
            setSubmitStatus(
              `${nextStatusLabel}${pdfUrl ? " - PDF ready" : ""}`,
            );
            setSubmitStatusUrl(pdfUrl || "");
          }
        } catch (err) {
          // keep submit success even when status check fails
        }
      }

      setSubmitDialog({
        open: true,
        type: "success",
        title: "Assessment submitted successfully!",
        lines: [
          "You may now safely close this browser tab.",
          "The probation assessment has been successfully sent to the supervisor and HR.",
          "You should receive the email within 5–10 minutes.",
        ],
      });
      setIsSubmitted(true);
    } catch (err) {
      const message = err?.message || "Submit failed";
      setSubmitStatus(message);
      setSubmitStatusUrl("");
      setIsSubmitted(false);
      setSubmitDialog({
        open: true,
        type: "error",
        title: "Submit failed",
        lines: [message],
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="probation-page" className="probation-page">
      {isPageLoading ? (
        <div
          className="probation-page-loading"
          role="status"
          aria-live="polite"
        >
          <div className="probation-page-loading-card">
            <span className="probation-page-spinner" />
            <span>
              {isLoadingProfile
                ? "Loading user profile..."
                : "Loading template..."}
            </span>
          </div>
        </div>
      ) : null}
      {submitDialog.open ? (
        <div
          className="probation-dialog-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="probation-dialog-card">
            <p className="probation-dialog-title">{submitDialog.title}</p>
            {submitDialog.lines.map((line) => (
              <p key={line} className="probation-dialog-line">
                {line}
              </p>
            ))}
            {submitDialog.type === "processing" ? (
              <span className="probation-dialog-spinner" aria-hidden="true" />
            ) : (
              <button
                type="button"
                className="probation-dialog-ok"
                onClick={closeSubmitDialog}
              >
                OK
              </button>
            )}
          </div>
        </div>
      ) : null}
      <header className="probation-header-bar">
        <div className="probation-header-inner">
          <img
            src="https://storage.googleapis.com/exo24_public/exo-icon-new.webp"
            alt="EXO Logo"
            className="probation-logo"
          />
          <h1>Probation Assessment</h1>
        </div>
      </header>

      <main className="probation-main">
        {isPageBlocked ? (
          <div className="probation-blocked">
            You cannot access this probation form.
          </div>
        ) : (
          <form
            ref={probationFormRef}
            className="probation-form"
            onSubmit={handleSubmit}
          >
            <section>
              <h2>Employee Details</h2>
              <div className="probation-grid-2">
                <InputWithIcon
                  label="Employee's Name"
                  icon="person"
                  name="employeeName"
                  value={formState.employeeName}
                  onChange={handleFieldChange}
                  placeholder="Enter name"
                />
                <InputWithIcon
                  label="Supervisor's Name"
                  icon="supervisor_account"
                  name="supervisorName"
                  value={formState.supervisorName}
                  onChange={handleFieldChange}
                  placeholder="Enter supervisor name"
                />
                <InputWithIcon
                  label="Job Title"
                  icon="work"
                  name="jobTitle"
                  value={formState.jobTitle}
                  onChange={handleFieldChange}
                  placeholder="Enter job title"
                />
                <InputWithIcon
                  label="Department"
                  icon="domain"
                  name="department"
                  value={formState.department}
                  onChange={handleFieldChange}
                  placeholder="Enter department"
                />
                <InputWithIcon
                  label="Start Date"
                  icon="event"
                  name="startDate"
                  type="date"
                  value={formState.startDate}
                  onChange={handleFieldChange}
                />
                <InputWithIcon
                  label="Meeting Date"
                  icon="schedule"
                  name="meetingDate"
                  type="date"
                  value={formState.meetingDate}
                  onChange={handleFieldChange}
                  required
                />
              </div>
              {!isLoadingProfile && isLoadingTemplate && (
                <p className="probation-loading">Loading template...</p>
              )}
            </section>
            <section>
              <h2>Evaluation</h2>
              <div
                className={`probation-table-wrap${validationErrors.evaluation ? " probation-table-wrap-error" : ""}`}
              >
                <table className="probation-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>EXO Value</th>
                      <th>Definition</th>
                      <th>Assessment</th>
                      <th>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluationRows.map((row) => {
                      const current =
                        evaluationState[row.key] ||
                        getInitialEvaluationRowState();

                      if (row.hasTrainingInput) {
                        return (
                          <tr key={row.key}>
                            <td>{row.no}</td>
                            <td>{row.title}</td>
                            <td colSpan={3}>
                              <textarea
                                className="probation-training-textarea"
                                rows={4}
                                placeholder="Please specify..."
                                value={current.trainingNeeds}
                                onChange={(evt) =>
                                  handleEvaluationChange(
                                    row.key,
                                    "trainingNeeds",
                                    evt.target.value,
                                  )
                                }
                              />
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr key={row.key}>
                          <td>{row.no}</td>
                          <td>{row.title}</td>
                          <td>
                            {row.definition && !row.hasRoleSelect ? (
                              <div className="def-text">{row.definition}</div>
                            ) : (
                              ""
                            )}

                            {row.hasRoleSelect && (
                              <div className="probation-role-wrap">
                                <div className="probation-label-mini">
                                  Please specify role:
                                </div>
                                <SelectWithArrow
                                  value={current.role || ""}
                                  required
                                  onChange={(evt) =>
                                    handleEvaluationChange(
                                      row.key,
                                      "role",
                                      evt.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select role</option>
                                  {jobRoleOptions.map((role) => (
                                    <option key={role} value={role}>
                                      {role}
                                    </option>
                                  ))}
                                </SelectWithArrow>
                              </div>
                            )}
                          </td>

                          <td>
                            {!row.hasRoleSelect && (
                              <SelectWithArrow
                                value={current.score || ""}
                                required
                                onChange={(evt) =>
                                  handleEvaluationChange(
                                    row.key,
                                    "score",
                                    evt.target.value,
                                  )
                                }
                              >
                                <option value="">Select assessment</option>
                                {assessmentOptions.map((opt) => (
                                  <option
                                    key={opt.label}
                                    value={
                                      opt.value === ""
                                        ? ASSESSMENT_NA_SENTINEL
                                        : opt.value
                                    }
                                  >
                                    {opt.label}
                                  </option>
                                ))}
                              </SelectWithArrow>
                            )}

                            {row.hasRoleSelect && (
                              <SelectWithArrow
                                value={current.jobSkill || ""}
                                required
                                onChange={(evt) =>
                                  handleEvaluationChange(
                                    row.key,
                                    "jobSkill",
                                    evt.target.value,
                                  )
                                }
                              >
                                <option value="">Select assessment</option>
                                {jobSkillAssessmentOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </SelectWithArrow>
                            )}
                          </td>

                          <td>
                            <textarea
                              className="probation-comment-textarea"
                              rows={2}
                              placeholder="Comments"
                              value={current.comment}
                              onChange={(evt) =>
                                handleEvaluationChange(
                                  row.key,
                                  "comment",
                                  evt.target.value,
                                )
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="probation-grid-2">
              <div>
                <h2>Additional Comment</h2>
                <textarea
                  className="probation-additional-textarea"
                  name="additionalComment"
                  rows={4}
                  value={formState.additionalComment}
                  onChange={handleFieldChange}
                  placeholder="Enter your additional comment here."
                />
              </div>

              <div>
                <h2>Probation Result</h2>
                <div className="probation-result-box">
                  <div>
                    <p className="result-title">Pass probation?</p>
                    <p
                      className={
                        formState.passProbation ? "result-yes" : "result-no"
                      }
                    >
                      {formState.passProbation ? "Yes" : "No"}
                    </p>
                  </div>

                  <div className="toggle-right">
                    <span className="probation-icon-wrap" aria-hidden="true">
                      {formState.passProbation ? (
                        <svg
                          className="probation-icon-check"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="probation-icon-cross"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      )}
                    </span>
                    <label className="probation-toggle-pill">
                      <input
                        type="checkbox"
                        checked={formState.passProbation}
                        onChange={(evt) =>
                          setFormState((prev) => ({
                            ...prev,
                            passProbation: evt.target.checked,
                          }))
                        }
                      />
                      <div className="slider" />
                    </label>
                  </div>
                </div>
              </div>
            </section>
            <section className="probation-ack">
              <h2>Evaluated and Acknowledged By</h2>
              <div className="probation-grid-3">
                <div>
                  <label>Supervisor or Manager</label>
                  <p className="probation-ack-value">
                    {profile?.fname + " " + profile?.lname || "-"}
                  </p>
                </div>
                <div>
                  <label>Email</label>
                  <p className="probation-ack-value">
                    {profile?.emailadd || "-"}
                  </p>
                </div>
                <div>
                  <label>Timestamp</label>
                  <p className="probation-ack-value">{evaluatedTimestamp}</p>
                </div>
              </div>
            </section>
            <div className="probation-submit-wrap">
              <button
                type="submit"
                className="btn-exo"
                disabled={isSubmitting || isSubmitted}
              >
                {isSubmitting
                  ? "Processing"
                  : isSubmitted
                    ? "Submitted"
                    : "Submit Assessment"}
              </button>
              {submitStatus ? (
                <p className="probation-loading" style={{ marginTop: "14px" }}>
                  {submitStatus}
                  {submitStatusUrl ? (
                    <>
                      {" "}
                      -{" "}
                      <a
                        href={submitStatusUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="probation-pdf-link"
                      >
                        Open PDF
                      </a>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>{" "}
          </form>
        )}
      </main>
    </div>
  );
};

function InputWithIcon({ label, icon, ...props }) {
  const Icon = getProbationIcon(icon);
  const isMeetingDateField = props.name === "meetingDate";
  return (
    <div>
      <label className="input-label">{label}</label>
      <div className="input-icon-wrap">
        {Icon ? (
          <Icon
            className="icon-left"
            size={18}
            strokeWidth={1.75}
            aria-hidden="true"
          />
        ) : null}
        <input
          className="browser-default w-full border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          readOnly={!isMeetingDateField}
          {...props}
        />
      </div>
    </div>
  );
}

function SelectWithArrow({ children, ...props }) {
  return (
    <div className="custom-select-wrapper">
      <select
        className="browser-default custom-select w-full rounded-md border border-gray-300 bg-white p-2 pr-8 text-sm text-gray-900 focus:border-[#66bfa1] focus:outline-none focus:ring-1 focus:ring-[#66bfa1]"
        {...props}
      >
        {children}
      </select>
      <span className="custom-select-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </span>
    </div>
  );
}

function loadExternalStyles() {
  ensureStylesheet(
    "https://fonts.googleapis.com/css2?family=Open+Sans&display=swap",
  );
  ensureTailwindCdn();
}

function getProbationIcon(name) {
  const iconMap = {
    person: User,
    supervisor_account: Users,
    work: Briefcase,
    domain: Building2,
    event: Calendar,
    schedule: Clock3,
  };
  return iconMap[name] || null;
}
function ensureTailwindCdn() {
  if (window.tailwind) return;
  if (!document.querySelector("script[data-tailwind-cdn='1']")) {
    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com";
    script.setAttribute("data-tailwind-cdn", "1");
    document.head.appendChild(script);
  }
}
function ensureStylesheet(href) {
  if (document.querySelector(`link[href='${href}']`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function pickValue(data, keys = []) {
  for (const key of keys) {
    const value = data?.[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return "";
}

function getDisplayName(data) {
  const fullName = pickValue(data, [
    "fullname",
    "full_name",
    "fullName",
    "name",
    "display_name",
    "displayName",
  ]);
  if (fullName) return fullName;
  const first = pickValue(data, ["fname", "first_name", "firstName"]);
  const last = pickValue(data, ["lname", "last_name", "lastName"]);
  return `${first} ${last}`.trim();
}

function normalizeToDateInput(value) {
  if (!value || typeof value !== "string") return "";
  const raw = value.trim();
  if (!raw) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) return raw.slice(0, 10);

  const dash = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dash) return `${dash[3]}-${dash[2]}-${dash[1]}`;

  const slash = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[3]}-${slash[2]}-${slash[1]}`;

  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

function mapProfileToForm(data) {
  return {
    employeeName: getDisplayName(data),
    supervisorName: getSupervisorDisplayName(data),
    jobTitle: pickValue(data, [
      "jobTitle",
      "position",
      "job_title",
      "title",
      "role",
    ]),
    department: pickValue(data, ["department", "dept_name", "department_name"]),
    startDate: normalizeToDateInput(
      pickValue(data, [
        "startDate",
        "employment_start_date",
        "start_date",
        "date_start",
        "hired_date",
      ]),
    ),
  };
}

function getSupervisorDisplayName(data) {
  const supervisorName = pickValue(data, [
    "supervisor",
    "supervisorName",
    "supervisor_name",
    "manager_name",
    "line_manager",
    "eleave_approver_name",
  ]);
  const coSupervisorName = pickValue(data, [
    "coSupervisor",
    "coSupervisorName",
    "co_supervisor",
    "co_supervisor_name",
  ]);

  if (supervisorName && coSupervisorName) {
    if (supervisorName.toLowerCase() === coSupervisorName.toLowerCase()) {
      return supervisorName;
    }
    return `${supervisorName} / ${coSupervisorName}`;
  }

  return supervisorName || coSupervisorName;
}
function formatNowTimestamp() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = now.toLocaleString("en-US", { month: "short" });
  const year = now.getFullYear();
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hour}:${minute}`;
}

function getInitialEvaluationRowState() {
  return {
    score: "",
    comment: "",
    role: "",
    jobSkill: "",
    trainingNeeds: "",
  };
}
function capitalizeFirstChar(text) {
  const value = String(text || "").trim();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function parseApiPayload(raw) {
  return raw;
}

function isSuccessPayload(payload) {
  if (!payload || typeof payload !== "object") return false;
  const data = payload?.data || payload;
  const errValue = data?.err ?? payload?.err ?? 0;
  const statusValue = data?.status ?? payload?.status;
  return Number(errValue) === 0 || statusValue === "success";
}

function normalizeOptions(input, fallback) {
  if (Array.isArray(input) && input.length > 0) {
    if (typeof input[0] === "object") {
      return input.map((item) => ({
        value: String(item?.value ?? item?.key ?? ""),
        label: String(
          item?.label ?? item?.name ?? item?.text ?? item?.value ?? "",
        ),
      }));
    }
    return input.map((value) => ({
      value: String(value),
      label: String(value),
    }));
  }

  if (input && typeof input === "object") {
    return Object.keys(input).map((key) => ({
      value: key,
      label: String(input[key]),
    }));
  }

  return fallback;
}

function mergeTemplateRows(baseRows, templateItems) {
  const byNo = {};
  baseRows.forEach((row) => {
    byNo[row.no] = row;
  });

  return templateItems.map((item) => {
    const no = Number(item?.no || item?.itemNo || 0);
    const matched = byNo[no];
    if (matched) {
      return {
        ...matched,
        title: item?.title || item?.name || matched.title,
        definition:
          item?.definition ??
          item?.description ??
          item?.label ??
          matched.definition,
      };
    }

    return {
      no,
      key: `item_${no}`,
      title: item?.title || item?.name || `Item ${no}`,
      definition: item?.definition || item?.description || "",
      hasRoleSelect: no === 8,
      hasTrainingInput: no === 9,
    };
  });
}
export default Probation;
